import React, { useState, useEffect, useContext } from 'react';
import { Form, Select, Row, Col, Divider, Button, Table, Input, DatePicker, InputNumber, Checkbox, message, Card, Spin, Drawer, Pagination } from 'antd';
import { PlusOutlined, DeleteOutlined, SearchOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import 'moment/locale/es';
import axios from 'axios';
import dayjs from 'dayjs';
import { LoginContext } from '../../../../context/loginContext';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

const CPagoAdministrador = () => {
    const [form] = Form.useForm();
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [selectedPaymentTypes, setSelectedPaymentTypes] = useState([]);
    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const { user, selectedEmpresa } = useContext(LoginContext);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [filters, setFilters] = useState({ cue_nombre: '', cue_cuit: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const pageSize = 5;
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        fetchPaymentTypes();
        fetchBanks();
    }, []);


    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page: currentPage,
                limit: pageSize,
                ...(filters.cue_cuit && { cue_cuit: filters.cue_cuit }),
                ...(filters.cue_nombre && { cue_nombre: filters.cue_nombre }),
            });

            const url = `${process.env.REACT_APP_API_URL}/cuenta/empresa/${selectedEmpresa.emp_id}?${queryParams.toString()}`;
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const response = await axios.get(url, config);

            setAccounts(response.data.cuentas);
            setTotalItems(response.data.total);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setAccounts([]); // Limpiar cuentas si no se encontraron
                setTotalItems(0);
                message.info('No se encontraron cuentas para esta empresa');
            } else {
                console.error('Error fetching accounts:', error);
                message.error('Error al cargar las cuentas');
            }
        } finally {
            setLoading(false);
        }
    };

    // Fetch payment types from API
    const fetchPaymentTypes = async () => {
        try {
            setLoading(true);
            const url = `${process.env.REACT_APP_API_URL}/tipo-pago`;
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const response = await axios.get(url, config);
            setPaymentTypes(response.data);
        } catch (error) {
            console.error('Error fetching payment types:', error);
            message.error('Error al cargar los tipos de pago');
        } finally {
            setLoading(false);
        }
    };

    // Fetch banks from API
    const fetchBanks = async () => {
        try {
            setLoading(true);
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/banco`, config);
            setBanks(response.data);
        } catch (error) {
            console.error('Error fetching banks:', error);
            message.error('Error al cargar los bancos');
        } finally {
            setLoading(false);
        }
    };

    const handleAddPayment = () => {
        if (paymentTypes.length > 0) {
            const newPayment = {
                id: Date.now(), // Temporary ID for frontend tracking
                pag_tipopago: paymentTypes[0].tpa_id,
                tpa_nombre: paymentTypes[0].tpa_nombre,
                pag_importe: 0,
                pag_referencia: '',
                pag_fecha: null,
                pag_banco: null,
                ban_nombre: '',
                pag_comprobante: '',
                pag_fechaorigen: null,
                pag_fechavencimiento: null,
                pag_acreditado: false
            };
            setSelectedPaymentTypes([...selectedPaymentTypes, newPayment]);
        }
    };

    // Remove a payment type from the list
    const handleRemovePayment = (paymentId) => {
        const updatedPayments = selectedPaymentTypes.filter(payment =>
            payment.id !== paymentId && payment.pag_id !== paymentId
        );
        setSelectedPaymentTypes(updatedPayments);
    };

    // Update a specific payment type's details
    const handleUpdatePayment = (paymentId, field, value) => {
        const updatedPayments = selectedPaymentTypes.map(payment => {
            if ((payment.id === paymentId) || (payment.pag_id === paymentId)) {
                const updatedPayment = { ...payment, [field]: value };

                // If updating payment type, also update its name
                if (field === 'pag_tipopago') {
                    const selectedType = paymentTypes.find(type => type.tpa_id === value);
                    if (selectedType) {
                        updatedPayment.tpa_nombre = selectedType.tpa_nombre;
                    }
                }

                // If updating bank, also update its name
                if (field === 'pag_banco') {
                    const selectedBank = banks.find(bank => bank.ban_id === value);
                    if (selectedBank) {
                        updatedPayment.ban_nombre = selectedBank.ban_nombre;
                    }
                }

                return updatedPayment;
            }
            return payment;
        });

        setSelectedPaymentTypes(updatedPayments);
    };

    // Reset the form and selected payment types
    const handleReset = () => {
        form.resetFields();
        setSelectedPaymentTypes([]);
    };

    const handleSelectAccount = (record) => {
        console.log('ID CUENTA', record.cue_id);
        form.setFieldsValue({
            account: record.cue_nombre, // Mostrar el nombre en el input visible
            accountId: record.cue_id, // Guardar el ID en un campo oculto
        });
        setSelectedAccount(record.cue_id)
        setDrawerVisible(false);
    };

    const handleFilterChange = (name, value) => {
        const newFilters = { ...filters, [name]: value };
        setFilters(newFilters);
        setCurrentPage(1); // Reiniciar a la primera página al filtrar
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Submit the form
    const onFinish = async (values) => {
        try {
            setSaving(true);


            if(selectedAccount === 0){
                message.warning('Debe seleccionar una cuenta');
                setSaving(false);
                return;
            }
            
            if (selectedPaymentTypes.length === 0) {
                message.warning('Debe agregar al menos un método de pago');
                setSaving(false);
                return;
            }

            // Validar que todos los métodos de pago tengan pag_tipopago y pag_importe
            const isValid = selectedPaymentTypes.every(pt =>
                pt.pag_tipopago !== undefined && pt.pag_tipopago !== null &&
                pt.pag_importe !== undefined && pt.pag_importe !== null && Number(pt.pag_importe) > 0 &&
                pt.pag_fechaorigen !== undefined && pt.pag_fechaorigen !== null
            );

            if (!isValid) {
                message.warning('Todos los métodos de pago deben incluir Tipo de Pago, Importe y Fecha Origen');
                setSaving(false);
                return;
            }

            if(values.transactionType === null){
                message.warning('Debe elegir si es INGRESO o EGRESO');
                setSaving(false);
                return;
            }
            // Formatear los métodos de pago para la API
            const formattedPaymentTypes = selectedPaymentTypes.map(pt => ({
                pag_tipopago: Number(pt.pag_tipopago),
                pag_importe: Number(pt.pag_importe),
                pag_banco: pt.pag_banco,
                pag_comprobante: pt.pag_comprobante,
                pag_fechaorigen: pt.pag_fechaorigen ? moment(pt.pag_fechaorigen, "YYYY-MM-DD") : null,
                pag_fechavencimiento: pt.pag_fechavencimiento ? moment(pt.pag_fechavencimiento, "YYYY-MM-DD") : null,
                pag_acreditado: pt.pag_acreditado,
                pag_cuenta: selectedAccount, // Asegúrate de que el campo "pag_cuenta" esté incluido si es necesario
                pag_ingegr: values.transactionType
            }));

            const payload = {
                ...values,
                payment_types: formattedPaymentTypes
            };

            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };

            // Enviar los datos a la API
            await axios.post(`${process.env.REACT_APP_API_URL}/pago`, payload, config);

            message.success('Métodos de pago guardados correctamente');
            handleReset();
        } catch (error) {
            console.error('Error saving payment types:', error);
            message.error('Error al guardar los métodos de pago');
        } finally {
            setSaving(false);
        }
    };

    // Define table columns
    const columns = [
        {
            title: 'Tipo de Pago',
            dataIndex: 'pag_tipopago',
            key: 'pag_tipopago',
            width: '15%',
            render: (tpa_id, record) => (
                <Select
                    style={{ width: '100%' }}
                    value={tpa_id || record.pag_tipopago}
                    onChange={(value) => handleUpdatePayment(record.id || record.pag_id, 'pag_tipopago', value)}
                >
                    {paymentTypes.map(type => (
                        <Option key={type.tpa_id} value={type.tpa_id}>
                            {type.tpa_nombre}
                        </Option>
                    ))}
                </Select>
            ),
        },
        {
            title: 'Monto',
            dataIndex: 'pag_importe',
            key: 'pag_importe',
            width: '12%',
            render: (amount, record) => (
                <InputNumber
                    style={{ width: '100%' }}
                    value={amount || record.pag_importe}
                    min={0}
                    step={0.01}
                    precision={2}
                    onChange={(value) => handleUpdatePayment(record.id || record.pag_id, 'pag_importe', value)}
                />
            ),
        },
        {
            title: 'Banco',
            dataIndex: 'pag_banco',
            key: 'pag_banco',
            width: '15%',
            render: (ban_id, record) => (
                <Select
                    style={{ width: '100%' }}
                    value={ban_id || record.pag_banco}
                    onChange={(value) => handleUpdatePayment(record.id || record.pag_id, 'pag_banco', value)}
                    allowClear
                    placeholder="Seleccionar banco"
                >
                    {banks.map(bank => (
                        <Option key={bank.ban_id} value={bank.ban_id}>
                            {bank.ban_nombre}
                        </Option>
                    ))}
                </Select>
            ),
        },
        {
            title: 'Num. Comprobante',
            dataIndex: 'pag_comprobante',
            key: 'pag_comprobante',
            width: '15%',
            render: (comprobante, record) => (
                <Input
                    value={comprobante || record.pag_comprobante}
                    onChange={(e) => handleUpdatePayment(record.id || record.pag_id, 'pag_comprobante', e.target.value)}
                    placeholder="Número/Referencia"
                />
            ),
        },
        {
            title: 'F. Origen',
            dataIndex: 'pag_fechaorigen',
            key: 'pag_fechaorigen',
            width: '12%',
            render: (date, record) => {
                let dateValue = date ? dayjs(date) : null;

                return (
                    <DatePicker
                        value={dateValue}
                        format={'DD/MM/YYYY'}
                        onChange={(value) => {
                            let formattedValue = value ? value.format('YYYY-MM-DD') : null;
                            handleUpdatePayment(record.id || record.pag_id, 'pag_fechaorigen', formattedValue);
                        }}
                        style={{ width: '100%' }}
                    />
                );
            }
        },
        {
            title: 'F. Vencimiento',
            dataIndex: 'pag_fechavencimiento',
            key: 'pag_fechavencimiento',
            width: '12%',
            render: (date, record) => {
                let dateValue = date ? dayjs(date) : null;

                return (
                    <DatePicker
                        value={dateValue}
                        format={'DD/MM/YYYY'}
                        onChange={(value) => {
                            let formattedValue = value ? value.format('YYYY-MM-DD') : null;
                            handleUpdatePayment(record.id || record.pag_id, 'pag_fechavencimiento', formattedValue);
                        }}
                        style={{ width: '100%' }}
                    />
                );
            }
        },
        {
            title: 'Acred.',
            dataIndex: 'pag_acreditado',
            key: 'pag_acreditado',
            width: '7%',
            align: 'center',
            render: (acreditado, record) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox
                        checked={acreditado || record.pag_acreditado}
                        onChange={(e) => handleUpdatePayment(record.id || record.pag_id, 'pag_acreditado', e.target.checked)}
                    />
                </div>
            ),
        },
        {
            title: '',
            key: 'action',
            width: '5%',
            render: (_, record) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemovePayment(record.id || record.pag_id)}
                    />
                </div>
            ),
        },
    ];

    const columnsAccount = [
        {
            title: 'Nombre',
            dataIndex: 'cue_nombre',
            key: 'cue_nombre',
            sorter: (a, b) => a.cue_nombre.localeCompare(b.cue_nombre),
            filterDropdown: () => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Buscar por nombre"
                        value={filters.cue_nombre}
                        onChange={e => handleFilterChange('cue_nombre', e.target.value)}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Button
                        type="primary"
                        onClick={() => fetchAccounts()}
                        size="small"
                        style={{ width: 90, marginRight: 8 }}
                    >
                        Buscar
                    </Button>
                    <Button
                        onClick={() => handleFilterChange('cue_nombre', '')}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Limpiar
                    </Button>
                </div>
            ),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        },
        {
            title: 'CUIT',
            dataIndex: 'cue_cuit',
            key: 'cue_cuit',
            filterDropdown: () => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Buscar por CUIT"
                        value={filters.cue_cuit}
                        onChange={e => handleFilterChange('cue_cuit', e.target.value)}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Button
                        type="primary"
                        onClick={() => fetchAccounts()}
                        size="small"
                        style={{ width: 90, marginRight: 8 }}
                    >
                        Buscar
                    </Button>
                    <Button
                        onClick={() => handleFilterChange('cue_cuit', '')}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Limpiar
                    </Button>
                </div>
            ),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        },
        {
            title: 'Tipo',
            dataIndex: 'cue_tipocuenta',
            key: 'cue_tipocuenta',
            render: (tipo) => {
                const tipoMap = {
                    'CF': 'Consumidor Final',
                    'EX': 'Exento',
                    'MON': 'Monotributo',
                    'RI': 'Responsable Inscripto'
                };
                return tipoMap[tipo] || tipo;
            }
        }
    ];

    return (
        <div>
            <Form form={form} layout="vertical" onFinish={onFinish}>
                {/* Any additional form fields can be added here */}
                <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
                    <Col span={16} style={{ display: 'flex', alignItems: 'center' }}>
                        <Form.Item
                            name="transactionType"
                            style={{ marginBottom: 0, width: '200px' }}
                            rules={[{ required: true, message: 'Seleccione un tipo de transacción' }]}
                        >
                            <Select placeholder="Tipo de Transacción">
                                <Option value={1}>EGRESO</Option>
                                <Option value={2}>INGRESO</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            name="account"
                            style={{ marginBottom: 0, flex: 1, marginRight: 5, marginLeft: 5 }}
                        >
                            <Input
                                placeholder="Ninguna cuenta seleccionada"
                                disabled
                                value={selectedAccount ? form.getFieldValue('account') : ''}
                            />
                            <Form.Item name="accountId" hidden={true}>
                                <Input />
                            </Form.Item>
                        </Form.Item>
                    </Col>
                    <Col span={8} style={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            type="primary"
                            onClick={() => {
                                fetchAccounts();
                                setDrawerVisible(true);
                            }}
                            style={{ width: '100%' }}
                        >
                            Seleccionar Cuenta
                        </Button>
                    </Col>
                </Row>

                <Divider orientation="left">Métodos de Pago</Divider>

                <Spin spinning={loading}>
                    <Table
                        columns={columns}
                        dataSource={selectedPaymentTypes.map(payment => ({
                            ...payment,
                            key: payment.id || payment.pag_id
                        }))}
                        pagination={false}
                        bordered
                        size="middle"
                    />
                </Spin>

                <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={24}>
                        <Button
                            type="dashed"
                            onClick={handleAddPayment}
                            style={{ width: '100%', marginBottom: 16 }}
                            icon={<PlusOutlined />}
                            disabled={loading || paymentTypes.length === 0}
                        >
                            Agregar Pago
                        </Button>
                    </Col>
                </Row>

                <Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                style={{ width: '100%' }}
                                loading={saving}
                            >
                                Guardar
                            </Button>
                        </Col>
                        <Col span={12}>
                            <Button
                                onClick={handleReset}
                                icon={<ReloadOutlined />}
                                style={{ width: '100%' }}
                                disabled={saving}
                            >
                                Limpiar
                            </Button>
                        </Col>
                    </Row>
                </Form.Item>
            </Form>
            <Drawer
                title="Seleccionar Cuenta"
                placement="bottom"
                onClose={() => setDrawerVisible(false)}
                visible={drawerVisible}
                height={500}
            >
                <div style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Input
                                placeholder="Buscar por nombre"
                                value={filters.cue_nombre}
                                onChange={e => handleFilterChange('cue_nombre', e.target.value)}
                                style={{ width: '100%' }}
                                suffix={<SearchOutlined />}
                            />
                        </Col>
                        <Col span={12}>
                            <Input
                                placeholder="Buscar por CUIT"
                                value={filters.cue_cuit}
                                onChange={e => handleFilterChange('cue_cuit', e.target.value)}
                                style={{ width: '100%' }}
                                suffix={<SearchOutlined />}
                            />
                        </Col>
                    </Row>
                    <Row style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
                        <Col>
                            <Button type="primary" onClick={fetchAccounts} style={{ marginRight: 8 }}>
                                Buscar
                            </Button>
                            <Button onClick={() => setFilters({ cue_nombre: '', cue_cuit: '' })}>
                                Limpiar Filtros
                            </Button>
                        </Col>
                        <Col>
                            <Button type="primary" onClick={() => navigate('/cuenta-administrador')}>
                                Cuentas
                            </Button>
                        </Col>
                    </Row>
                </div>

                {loading ? (
                    <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />
                ) : (
                    <>
                        <Table
                            dataSource={accounts}
                            columns={columnsAccount}
                            rowKey="cue_id"
                            pagination={false}
                            onRow={(record) => ({
                                onClick: () => handleSelectAccount(record),
                                style: { cursor: 'pointer' }
                            })}
                            scroll={{ y: 240 }}
                        />
                        <Pagination
                            current={currentPage}
                            total={totalItems}
                            pageSize={pageSize}
                            onChange={handlePageChange}
                            style={{ marginTop: 16, textAlign: 'center' }}
                        />
                    </>
                )}
            </Drawer>
        </div>
    );
};

export default CPagoAdministrador;